<template>
  <div>
    <v-col
      cols="9"
      v-if="loading"
      class="mx-auto loading-progress"
      justify-self="center"
    >
      <v-progress-linear
        rounded
        height="6"
        indeterminate
        color="orange accent-4"
      ></v-progress-linear>
      <p class="text-center my-5">Loading..</p>
    </v-col>
    <div v-else>
      <h2>
        Track Manuscript &mdash;
        {{ `JMESR/${manuscript.folder._id}` }}
        <br />
        <small v-if="manuscript.submittedAt" class="grey--text">
          Submitted on {{ formatDate(manuscript.submittedAt) }}
        </small>
      </h2>

      <v-row class="mt-5">
        <v-col cols="12" md="8">
          <v-alert
            border="top"
            type="error"
            class="mb-5 pt-7 pb-5"
            elevation="2"
            prominent
            color="red"
            v-if="showOutdatedWarning"
          >
            <div
              :class="$vuetify.breakpoint.mdAndUp ? 'd-flex' : ''"
              class="ma-0"
            >
              This is an older version of this manuscript
              <v-btn
                small
                light
                depressed
                class="ml-auto"
                v-if="manuscript.newestVersion"
                :to="`/manuscripts/submitted/track/${manuscript.newestVersion}`"
              >
                See newest version
              </v-btn>
            </div>
          </v-alert>

          <v-card>
            <!-- We could also enable this if manuscript.decision == 'RESUBMIT' -->
            <div
              class="pa-3 blue lighten-4"
              v-if="
                !manuscript.newestVersion &&
                (manuscriptStatus == ManuscriptStatus.minorRevision ||
                  manuscriptStatus == ManuscriptStatus.majorRevision ||
                  manuscriptStatus == ManuscriptStatus.resubmissionRequested)
              "
            >
              <div class="d-flex">
                <v-btn
                  rounded
                  class="white--text mx-auto my-2"
                  color="blue darken-3"
                  @click="reviseManuscript"
                  :disabled="
                    (manuscript.newestVersion &&
                      manuscript.newestVersion != manuscript._id) ||
                    revising
                  "
                >
                  Click Here to Submit Revision
                </v-btn>
              </div>
            </div>

            <div class="pa-4">
              <div
                :class="$vuetify.breakpoint.mdAndUp ? 'd-flex' : ''"
                class="mb-4"
              >
                <v-chip outlined small class="mb-1 mr-2">
                  {{ manuscript.type }}
                </v-chip>

                <v-chip class="mb-1 mr-2" small>
                  Status: {{ manuscriptStatus }}
                </v-chip>

                <v-chip
                  small
                  class="mb-1 mr-2"
                  v-if="manuscript.decision"
                  :color="decisionChip.color"
                >
                  Decision: {{ decisionChip.decision }}&nbsp;
                  <v-icon>{{ decisionChip.icon }}</v-icon>
                </v-chip>
                <v-spacer></v-spacer>
              </div>
              <h2
                style="line-height: 1.2"
                class="paper-title green--text text--darken-3"
              >
                {{ manuscript.title }}
              </h2>
            </div>
            <v-divider></v-divider>
            <v-card-text>
              <h4>Abstract</h4>
              <div class="abstract">
                <div id="abstract" v-html="manuscript.abstract"></div>
              </div>
              <p>
                <strong class="label mr-2">Keywords:</strong>
                <v-chip
                  small
                  :key="word"
                  color="green lighten-4"
                  class="mr-1 my-1"
                  v-for="word in manuscript.keywords"
                >
                  {{ word }}
                </v-chip>
              </p>
            </v-card-text>
            <v-card-actions
              class="
                lime
                lighten-4
                text-center
                justify-center
                green--text
                text--darken-3
              "
              v-if="manuscript.special"
            >
              <h5 class="my-1 text-uppercase text--center">
                {{ manuscript.special.title }}
              </h5>
            </v-card-actions>
          </v-card>

          <v-card class="my-6">
            <v-card-title> Authors </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <ol>
                <li v-for="(author, index) in manuscript.authors" :key="index">
                  <AuthorDetails :author="author" />
                </li>
              </ol>
            </v-card-text>
          </v-card>

          <v-card class="my-6">
            <v-card-title> Uploaded Files </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <ol class="my-4" v-if="manuscript.files.length > 0">
                <li v-for="(file, index) in manuscript.files" :key="index">
                  <h3 class="d-flex">
                    {{ file.name }}
                    <v-spacer></v-spacer>
                    <v-btn
                      x-small
                      outlined
                      color="teal"
                      class="mr-1 mb-1"
                      :href="file.location"
                      :download="manuscript.title"
                    >
                      Download
                    </v-btn>
                  </h3>
                  <address>
                    <p v-if="file.description">{{ file.description }}</p>
                    <p>{{ formatDate(file.createdAt) }}</p>
                  </address>
                </li>
              </ol>
            </v-card-text>
          </v-card>

          <h2 class="text-center">Communications / Reviews</h2>
          <ManuscriptCommunications
            author
            :manuscript="manuscriptId"
            :current="manuscript.currentVersion"
          />
        </v-col>

        <v-col cols="12" md="4">
          <ManuscriptVersions :versions="versions" :manuscript="manuscript" />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { formatDate } from "../../tools/format";
import { required } from "../../tools/rules";
import { decisionChip } from "../../tools/decision-chip";

import { manuscriptStatus as ManuscriptStatus } from "../../tools/manuscript";

import AuthorDetails from "../Author/Includes/AuthorDetails";
import ManuscriptCommunications from "../Communication/Includes/ManuscriptCommunications";
import ManuscriptVersions from "../Editorial/Includes/ManuscriptVersions.vue";

export default {
  props: ["manuscriptId"],

  components: {
    AuthorDetails,
    ManuscriptVersions,
    ManuscriptCommunications,
  },

  data: () => ({
    manuscript: {
      _id: 0,
      abstract: "",
      files: [],
      invitations: [],
      currentVersion: false,
      folder: { _id: null },
    },
    communications: [],
    statusFormValid: true,
    manuscriptStatus: "INCOMPLETE",
    ManuscriptStatus,
    manuscriptDecision: null,
    required,
    revising: false,
    loading: false,
    versions: []
  }),

  computed: {
    decisionChip() {
      return decisionChip(this.manuscript.decision);
    },
    showOutdatedWarning() {
      return !this.manuscript.currentVersion;
    },
  },

  methods: {
    formatDate,
    fetchManuscript() {
      if (this.manuscriptId && !this.loading) {
        this.loading = true;

        this.$axios
          .get(`/manuscript/${this.manuscriptId}`)
          .then(async ({ data: response }) => {
            this.$bus.$emit("snackbar", {
              type: "info",
              message: "Manuscript loaded",
            });

            this.manuscript = response;
            this.communications = response.communications;
            response.manuscriptStatus &&
              (this.manuscriptStatus = response.manuscriptStatus);

            const { data: versions } = await this.$axios.get(`/manuscripts/versions/${response.folder._id}`);
            
            this.versions = versions;
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not load manuscript",
            });
            this.$router.go(-1);
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    reviseManuscript() {
      if (this.manuscriptId && !this.revising) {
        this.revising = true;
        this.$axios
          .post(`/manuscript/revise`, { manuscript: this.manuscriptId })
          .then(({ data: response }) => {
            this.$bus.$emit("manuscript.refresh");

            this.$router.push({
              path: `/manuscripts/continue/${response._id}`,
            });

            this.$bus.$emit("snackbar", {
              type: "info",
              message: "Please continue revision",
            });
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not start revision",
            });
          })
          .finally(() => {
            this.revising = false;
          });
      }
    },
  },
  mounted() {
    this.fetchManuscript();
  },
};
</script>

<style lang="scss" scoped>
.paper-title {
  word-break: keep-all;
}

.loading-progress {
  margin-top: 35vh;
}
</style>
